import React, { useState, useEffect } from 'react';
import { companyService } from '../../../services/companyService';
import Button from '../../../ui/Button';
import IconButton from '../../../ui/IconButton';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import DataTable from '../../../ui/DataTable/DataTable';
import { CellText, CellId, CellPhone } from '../../../ui/DataTable/cells';
import Card from '../../../ui/Card';
import { Field, Input, Textarea, FormGrid } from '../../../ui/Field/Field';
import { confirmDialog } from '../../../ui/feedback';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { usePermissions } from '../../../hooks/usePermissions';

const STATUS_STYLE = {
    success: { cls: 'bg-green-50 border-green-200 text-green-700', glyph: ICONS.success },
    error: { cls: 'bg-red-50 border-red-200 text-red-700', glyph: ICONS.warning },
    info: { cls: 'bg-blue-50 border-blue-200 text-blue-700', glyph: ICONS.info },
};

const CompanyManager = () => {
    // adding / editing / deleting a company: its own permission (permissions phase 4, 4 Oct 2026); the list stays the tab's
    const canManage = usePermissions().hasPermission('manage_invoice_companies');
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        trn: '',
        email: '',
        phone: ''
    });
    const [status, setStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        setLoading(true);
        const data = await companyService.getAllCompanies();
        setCompanies(data);
        setLoading(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleEdit = (company) => {
        setEditingId(company.id);
        setFormData({
            name: company.name,
            address: company.address || '',
            trn: company.trn || '',
            email: company.email || '',
            phone: company.phone || ''
        });
    };

    const handleCancel = () => {
        setEditingId(null);
        setFormData({
            name: '',
            address: '',
            trn: '',
            email: '',
            phone: ''
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.name) return;

        setIsSaving(true);
        setStatus({ type: 'info', message: 'Saving...' });
        const result = await companyService.saveCompany({ ...formData, id: editingId });
        if (result.success) {
            await fetchCompanies();
            handleCancel();
            setStatus({ type: 'success', message: 'Company saved successfully!' });
            // Clear success message after 3 seconds
            setTimeout(() => setStatus({ type: '', message: '' }), 3000);
        } else {
            setStatus({ type: 'error', message: 'Failed to save company. Please check permissions.' });
        }
        setIsSaving(false);
    };

    const handleDelete = async (id) => {
        if (!(await confirmDialog({
            title: 'Delete company?',
            message: 'Are you sure you want to delete this company?',
            confirmLabel: 'Delete',
            danger: true
        }))) return;
        setStatus({ type: 'info', message: 'Deleting...' });
        const result = await companyService.deleteCompany(id);
        if (result.success) {
            await fetchCompanies();
            setStatus({ type: 'success', message: 'Company deleted successfully!' });
            setTimeout(() => setStatus({ type: '', message: '' }), 3000);
        } else {
            setStatus({ type: 'error', message: 'Failed to delete company.' });
        }
    };

    if (loading) return <LoadingSpinner />;

    const statusStyle = STATUS_STYLE[status.type] || STATUS_STYLE.info;

    const columns = [
        {
            key: 'name',
            header: 'Name',
            cell: (company) => <CellText>{company.name}</CellText>,
        },
        {
            key: 'trn',
            header: 'TRN',
            cell: (company) => <CellId>{company.trn}</CellId>,
        },
        {
            key: 'email',
            header: 'Email/Phone',
            accessor: (company) => `${company.email || ''} ${company.phone || ''}`.trim(),
            cell: (company) => (
                <div className="flex flex-col items-center">
                    <CellText>{company.email}</CellText>
                    {company.phone && <CellPhone>{company.phone}</CellPhone>}
                </div>
            ),
        },
        {
            key: 'address',
            header: 'Address',
            cell: (company) => (
                <CellText max={320}>{company.address}</CellText>
            ),
        },
        {
            key: 'actions',
            header: 'Actions',
            sortable: false,
            searchable: false,
            cell: (company) => (
                canManage ? <div className="flex justify-center gap-1">
                    <IconButton size="sm" icon={ICONS.edit} label="Edit" onClick={() => handleEdit(company)} />
                    <IconButton size="sm" icon={ICONS.delete} label="Delete" tone="danger" onClick={() => handleDelete(company.id)} />
                </div> : null
            ),
        },
    ];

    return (
        <div className="space-y-6">
            <h2 className="text-base font-semibold text-gray-900">Manage Registered Companies</h2>

            {status.message && (
                <div className={`flex items-center gap-2 p-3 rounded-lg border text-sm ${statusStyle.cls}`}
                    role={status.type === 'error' ? 'alert' : 'status'}>
                    <Icon glyph={statusStyle.glyph} size="sm" />
                    <span>{status.message}</span>
                </div>
            )}

            {/* Form */}
            {canManage && <Card title={editingId ? 'Edit Company' : 'Add New Company'}>
            <form onSubmit={handleSave} className="space-y-4">
                <FormGrid cols={2}>
                    <Field label="Company Name" required>
                        <Input name="name" value={formData.name} onChange={handleInputChange} />
                    </Field>
                    <Field label="TRN Number">
                        <Input name="trn" value={formData.trn} onChange={handleInputChange} />
                    </Field>
                    <Field label="Email Address">
                        <Input type="email" name="email" value={formData.email} onChange={handleInputChange} />
                    </Field>
                    <Field label="Phone Number">
                        <Input name="phone" value={formData.phone} onChange={handleInputChange} />
                    </Field>
                </FormGrid>
                <Field label="Address">
                    <Textarea name="address" value={formData.address} onChange={handleInputChange} rows={2} />
                </Field>
                <div className="flex flex-wrap justify-end gap-2">
                    {editingId && (
                        <Button type="button" variant="secondary" onClick={handleCancel}>
                            Cancel
                        </Button>
                    )}
                    <Button type="submit" variant="primary" isLoading={isSaving}>
                        {isSaving ? 'Saving...' : editingId ? 'Update Company' : 'Register Company'}
                    </Button>
                </div>
            </form>
            </Card>}

            {/* Table */}
            <DataTable
                columns={columns}
                data={companies}
                rowKey="id"
                searchPlaceholder="Search companies…"
                emptyTitle="No companies registered yet."
            />
        </div>
    );
};

export default CompanyManager;
